import { api } from '../../services/api';
import { store } from '../../store';

export const settingsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // Get all settings
    getSettings: builder.query({
      query: (userId) => `/settings?userId=${userId}`,
      providesTags: ['Settings'],
      keepUnusedDataFor: 300,
    }),

    // Get notifications settings
    getNotifications: builder.query({
      query: () => '/settings/notifications',
      providesTags: ['Notifications'],
      keepUnusedDataFor: 300,
    }),

    // Get invoices list
    getInvoices: builder.query({
      query: () => '/invoices',
      providesTags: ['Invoices'],
      keepUnusedDataFor: 300,
    }),

    // Update account profile
    updateAccount: builder.mutation({
      query: (accountForm) => ({
        url: '/users/info',
        method: 'PUT',
        body: accountForm,
      }),
      invalidatesTags: ['Settings', 'User'],
    }),

    // Change password
    changePassword: builder.mutation({
      query: (passwordData) => ({
        url: '/users/password',
        method: 'PUT',
        body: passwordData,
      }),
    }),

    // Update payment information
    updatePayment: builder.mutation({
      query: (paymentForm) => ({
        url: '/settings/payment',
        method: 'PUT',
        body: paymentForm,
      }),
      invalidatesTags: ['Settings'],
    }),

    // Update invoicing settings
    updateInvoicing: builder.mutation({
      query: (invoicingForm) => ({
        url: '/settings/invoicing',
        method: 'PUT',
        body: invoicingForm,
      }),
      invalidatesTags: ['Settings'],
    }),

    // Update fiscal settings
    updateFiscal: builder.mutation({
      query: (fiscalForm) => ({
        url: '/settings/fiscal',
        method: 'PUT',
        body: fiscalForm,
      }),
      invalidatesTags: ['Settings'],
    }),

    // Update API connection
    updateApiConnection: builder.mutation({
      query: ({ connectionId, apiKey, status }) => ({
        url: '/settings/api-connection',
        method: 'PUT',
        body: {
          connection_id: connectionId,
          api_key: apiKey,
          status,
        },
      }),
      invalidatesTags: ['ApiConnections', 'Settings'],
    }),

    // Download invoice PDF as blob
    downloadInvoice: builder.query({
      query: (invoiceId) => ({
        url: `/invoices/${invoiceId}/download`,
        responseHandler: (response) => response.blob(),
      }),
    }),

    // Upload company logo (uses FormData)
    uploadLogo: builder.mutation({
      query: (formData) => ({
        url: '/settings/company/logo',
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['Settings', 'User'],
    }),

    // Delete company logo
    deleteLogo: builder.mutation({
      query: () => ({
        url: '/settings/company/logo',
        method: 'DELETE',
      }),
      invalidatesTags: ['Settings', 'User'],
    }),

    // Update email notification settings
    updateNotifications: builder.mutation({
      query: (newNotifications) => ({
        url: '/settings/notifications',
        method: 'PUT',
        body: newNotifications,
      }),
      invalidatesTags: ['Notifications', 'Settings'],
    }),

    // Update user subscription plan
    updatePlan: builder.mutation({
      query: (plan) => ({
        url: '/settings/change-plan',
        method: 'POST',
        body: { plan },
      }),
      invalidatesTags: ['User', 'Settings'],
    }),
  }),
  overrideExisting: true,
});

// Export RTK Query hooks for future usage
export const {
  useGetSettingsQuery,
  useGetNotificationsQuery,
  useGetInvoicesQuery,
  useUpdateAccountMutation,
  useChangePasswordMutation,
  useUpdatePaymentMutation,
  useUpdateInvoicingMutation,
  useUpdateFiscalMutation,
  useUpdateApiConnectionMutation,
  useDownloadInvoiceQuery,
  useUploadLogoMutation,
  useDeleteLogoMutation,
  useUpdateNotificationsMutation,
  useUpdatePlanMutation,
} = settingsApi;

// Export async functions wrapping RTK Query initiate actions for compatibility with existing components
export const getSettings = async (userId) => {
  return store.dispatch(settingsApi.endpoints.getSettings.initiate(userId)).unwrap();
};

export const getNotifications = async () => {
  return store.dispatch(settingsApi.endpoints.getNotifications.initiate()).unwrap();
};

export const getInvoices = async () => {
  return store.dispatch(settingsApi.endpoints.getInvoices.initiate()).unwrap();
};

export const updateAccount = async (accountForm) => {
  return store.dispatch(settingsApi.endpoints.updateAccount.initiate(accountForm)).unwrap();
};

export const changePassword = async (passwordData) => {
  return store.dispatch(settingsApi.endpoints.changePassword.initiate(passwordData)).unwrap();
};

export const updatePayment = async (paymentForm) => {
  return store.dispatch(settingsApi.endpoints.updatePayment.initiate(paymentForm)).unwrap();
};

export const updateInvoicing = async (invoicingForm) => {
  return store.dispatch(settingsApi.endpoints.updateInvoicing.initiate(invoicingForm)).unwrap();
};

export const updateFiscal = async (fiscalForm) => {
  return store.dispatch(settingsApi.endpoints.updateFiscal.initiate(fiscalForm)).unwrap();
};

export const updateApiConnection = async (connectionId, apiKey, status) => {
  return store.dispatch(settingsApi.endpoints.updateApiConnection.initiate({ connectionId, apiKey, status })).unwrap();
};

export const downloadInvoice = async (invoiceId) => {
  return store.dispatch(settingsApi.endpoints.downloadInvoice.initiate(invoiceId)).unwrap();
};

export const uploadLogo = async (formData) => {
  return store.dispatch(settingsApi.endpoints.uploadLogo.initiate(formData)).unwrap();
};

export const deleteLogo = async () => {
  return store.dispatch(settingsApi.endpoints.deleteLogo.initiate()).unwrap();
};

export const updateNotifications = async (newNotifications) => {
  return store.dispatch(settingsApi.endpoints.updateNotifications.initiate(newNotifications)).unwrap();
};

export const updatePlan = async (plan) => {
  return store.dispatch(settingsApi.endpoints.updatePlan.initiate(plan)).unwrap();
};
